import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// import '../pages/Landing.css'; // Inherits styles from Landing.css, assuming it's imported in parent or globally. 
// We might need to ensure styles are available. Since it is used in Landing page which imports Landing.css, it should be fine.

import ToggleSwitch from './ToggleSwitch';

const LandingAuth = ({ idSuffix = '' }) => {
    const navigate = useNavigate();
    const { login, register, user } = useAuth();
    const [activeTab, setActiveTab] = useState('login');
    const containerRef = useRef(null);
    const isFirstRender = useRef(true);

    // Scroll slightly up or to top of container when switching tabs to ensure visibility
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (containerRef.current) {
            const element = containerRef.current;
            // We can scroll the element into view if it's not fully visible
            // But we need to use a slight delay because state update might change height
            setTimeout(() => {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [activeTab]);

    // Login form state
    const [loginData, setLoginData] = useState({
        email: '',
        password: ''
    });

    // Register form state
    const [registerData, setRegisterData] = useState({
        email: '',
        password: '',
        companyName: '',
        acceptedTerms: false
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const result = await login(loginData.email, loginData.password);
        setLoading(false);

        if (result.success) {
            // Get user data from localStorage after successful login
            const savedUser = localStorage.getItem('user');
            let userData = {};
            try {
                if (savedUser && savedUser !== 'undefined') {
                    userData = JSON.parse(savedUser);
                }
            } catch (err) {
                console.error('Error parsing user data in login:', err);
                localStorage.removeItem('user');
            }

            // Redirect based on user role
            if (userData.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/dashboard');
            }
        } else {
            setError(result.message);
            setShowForgotPassword(true);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Prepare data with conditional fields
        const userData = {
            email: registerData.email,
            password: registerData.password,
            companyName: registerData.companyName,
            isCompany: registerData.isCompany,
            nip: registerData.isCompany ? registerData.nip : undefined,
            address: registerData.isCompany ? registerData.address : undefined,
            acceptedTerms: registerData.acceptedTerms
        };

        const result = await register(userData);
        console.log('Registration result (Debug):', result);
        setLoading(false);

        if (result.success) {
            if (result.isWaitingList) {
                // User added to waiting list
                setSuccess(result.message);
                // Clear form
                setRegisterData({
                    email: '',
                    password: '',
                    companyName: '',
                    isCompany: false,
                    nip: '',
                    address: { street: '', city: '', zipCode: '' },
                    acceptedTerms: false
                });
                // Do NOT redirect
            } else if (result.requiresVerification) {
                // Email verification required - show success message
                setSuccess(result.message + ' Sprawdź swoją skrzynkę: ' + result.email);
                // Clear form
                setRegisterData({
                    email: '',
                    password: '',
                    companyName: '',
                    isCompany: false,
                    nip: '',
                    address: { street: '', city: '', zipCode: '' },
                    acceptedTerms: false
                });
                // Switch to login tab after 5 seconds
                setTimeout(() => {
                    setActiveTab('login');
                    setSuccess('');
                }, 5000);
            } else {
                // Old flow - auto-login (backward compatibility)
                const savedUser = localStorage.getItem('user');
                let userData = {};
                try {
                    if (savedUser && savedUser !== 'undefined') {
                        userData = JSON.parse(savedUser);
                    }
                } catch (err) {
                    console.error('Error parsing user data:', err);
                    localStorage.removeItem('user');
                }

                setSuccess('Konto utworzone pomyślnie! Przekierowujemy...');
                setTimeout(() => {
                    if (userData.role === 'admin') {
                        navigate('/admin');
                    } else {
                        navigate('/dashboard');
                    }
                }, 1500);
            }
        } else {
            setError(result.message);
        }
    };

    return (
        <div className="auth-container" ref={containerRef}>
            <div className="auth-header">
                <h2>Poznaj wszystkie możliwości w wersji testowej</h2>
                <p>Ilość użytkowników ograniczona. Dołącz już dziś!</p>
            </div>

            <div className="auth-tabs">
                <button
                    className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
                    onClick={() => setActiveTab('login')}
                >
                    Logowanie
                </button>
                <button
                    className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
                    onClick={() => setActiveTab('register')}
                >
                    Rejestracja
                </button>
            </div>

            {error && <div className="auth-error">{error}</div>}
            {success && <div className="auth-success">{success}</div>}

            {activeTab === 'login' && (
                <form className="auth-form" onSubmit={handleLogin}>
                    <div className="form-group">
                        <label htmlFor={`login-email${idSuffix}`}>Email</label>
                        <input
                            type="email"
                            id={`login-email${idSuffix}`}
                            value={loginData.email}
                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                            required
                            placeholder="twoj@email.com"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor={`login-password${idSuffix}`}>Hasło</label>
                        <input
                            type="password"
                            id={`login-password${idSuffix}`}
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            required
                            placeholder="••••••••"
                        />
                        <div className="text-right mt-sm">
                            {showForgotPassword && (
                                <button
                                    type="button"
                                    className="btn-link text-small"
                                    onClick={() => navigate('/forgot-password')}
                                    style={{ background: 'none', border: 'none', color: 'var(--primary-dark)', cursor: 'pointer', fontSize: '0.9rem', padding: 0 }}
                                >
                                    Zapomniałeś hasła?
                                </button>
                            )}
                        </div>
                    </div>

                    <button type="submit" className="auth-submit" disabled={loading}>
                        {loading ? 'Logowanie...' : 'Zaloguj się'}
                    </button>
                </form>
            )}

            {activeTab === 'register' && (
                <form className="auth-form" onSubmit={handleRegister}>
                    <div className="form-group">
                        <label htmlFor={`register-email${idSuffix}`}>Email</label>
                        <input
                            type="email"
                            id={`register-email${idSuffix}`}
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            required
                            placeholder="twoj@email.com"
                        />
                    </div>

                    <ToggleSwitch
                        id={`register-isCompany${idSuffix}`}
                        checked={registerData.isCompany || false}
                        onChange={(e) => setRegisterData({
                            ...registerData,
                            isCompany: e.target.checked,
                            // Initialize address object if checked
                            address: e.target.checked ? { street: '', city: '', zipCode: '' } : undefined
                        })}
                        label="Konto firmowe (Dane do faktury)"
                    />

                    <div className="form-group">
                        <label htmlFor={`register-company${idSuffix}`}>
                            {registerData.isCompany ? 'Nazwa firmy' : 'Imię i nazwisko'}
                        </label>
                        <input
                            type="text"
                            id={`register-company${idSuffix}`}
                            value={registerData.companyName}
                            onChange={(e) => setRegisterData({ ...registerData, companyName: e.target.value })}
                            required
                            placeholder={registerData.isCompany ? "Twoja Firma Sp. z o.o." : "Jan Kowalski"}
                        />
                    </div>

                    {registerData.isCompany && (
                        <>
                            <div className="form-group">
                                <label htmlFor={`register-nip${idSuffix}`}>NIP</label>
                                <input
                                    type="text"
                                    id={`register-nip${idSuffix}`}
                                    value={registerData.nip || ''}
                                    onChange={(e) => setRegisterData({ ...registerData, nip: e.target.value })}
                                    required
                                    placeholder="1234567890"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor={`register-street${idSuffix}`}>Ulica i numer</label>
                                <input
                                    type="text"
                                    id={`register-street${idSuffix}`}
                                    value={registerData.address?.street || ''}
                                    onChange={(e) => setRegisterData({
                                        ...registerData,
                                        address: { ...registerData.address, street: e.target.value }
                                    })}
                                    required
                                    placeholder="ul. Przykładowa 1/2"
                                />
                            </div>
                            <div className="company-fields-grid">
                                <div className="form-group">
                                    <label htmlFor={`register-zip${idSuffix}`}>Kod pocztowy</label>
                                    <input
                                        type="text"
                                        id={`register-zip${idSuffix}`}
                                        value={registerData.address?.zipCode || ''}
                                        onChange={(e) => setRegisterData({
                                            ...registerData,
                                            address: { ...registerData.address, zipCode: e.target.value }
                                        })}
                                        required
                                        placeholder="00-000"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor={`register-city${idSuffix}`}>Miasto</label>
                                    <input
                                        type="text"
                                        id={`register-city${idSuffix}`}
                                        value={registerData.address?.city || ''}
                                        onChange={(e) => setRegisterData({
                                            ...registerData,
                                            address: { ...registerData.address, city: e.target.value }
                                        })}
                                        required
                                        placeholder="Warszawa"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="form-group">
                        <label htmlFor={`register-password${idSuffix}`}>Hasło</label>
                        <input
                            type="password"
                            id={`register-password${idSuffix}`}
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            required
                            placeholder="••••••••"
                            minLength="6"
                        />
                    </div>

                    <div className="form-checkbox-group terms-checkbox">
                        <input
                            type="checkbox"
                            id={`register-terms${idSuffix}`}
                            checked={registerData.acceptedTerms}
                            onChange={(e) => setRegisterData({ ...registerData, acceptedTerms: e.target.checked })}
                            required
                        />
                        <label htmlFor={`register-terms${idSuffix}`}>
                            Akceptuję <a href="/terms">Regulamin</a> i <a href="/privacy">Politykę Prywatności (RODO)</a>
                        </label>
                    </div>

                    <button type="submit" className="auth-submit" disabled={loading}>
                        {loading ? 'Rejestrowanie...' : 'Utwórz konto'}
                    </button>
                </form>
            )}
        </div>
    );
};

export default LandingAuth;
